import { createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { ConfirmModal } from "@/components/ConfirmModal.tsx";
import { Trash2 } from "lucide-react";
import { ApiError, UnauthorizedError, apiDelete, apiGet } from "@/lib/api.ts";
import { relativeTime } from "@/lib/format.ts";

// FRE-292: token management — list active PATs + soft-revoke.
//
// Session-cookie auth via the worker route. Mint UI lives in the Get started
// drawer (FRE-291); this surface is list + revoke only. Revoked tokens drop
// out of the list — the audit trail is on a separate (future) page.

type TokenItem = {
  id: string;
  name: string | null;
  scopes: string[];
  organizationId: string | null;
  organizationSlug: string | null;
  organizationName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  enabled: boolean;
  expiresAt: string | null;
};

type TokensResponse = { items: TokenItem[] };

type Me = { userId: string };

export const Route = createFileRoute("/settings/tokens")({
  beforeLoad: async () => {
    // Cheap auth gate identical to `/` and `/org/members` — bounce to /login
    // on 401 so the page doesn't render an empty shell behind a sign-in prompt.
    try {
      await apiGet<Me>("/api/v1/me");
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({ to: "/login", search: { next: "/settings/tokens" } });
      }
      throw err;
    }
  },
  component: TokensComponent,
});

function SkeletonRow() {
  return (
    <div className="grid grid-cols-[1.5fr_1fr_1.5fr_auto_auto_auto] items-center gap-4 px-4 py-3 border-b border-border last:border-b-0">
      <div className="h-4 rounded bg-muted animate-pulse" />
      <div className="h-4 rounded bg-muted animate-pulse" />
      <div className="h-4 rounded bg-muted animate-pulse" />
      <div className="h-4 w-16 rounded bg-muted animate-pulse" />
      <div className="h-4 w-16 rounded bg-muted animate-pulse" />
      <div className="h-6 w-20 rounded bg-muted animate-pulse" />
    </div>
  );
}

function TokensComponent() {
  const [items, setItems] = useState<TokenItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<TokensResponse>("/api/v1/me/tokens");
      setItems(res.items);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load tokens";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Tokens</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Personal access tokens (PATs) used by the CLI and MCP clients. Mint new tokens from the
          Get started drawer.
        </p>
      </div>

      {error ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="rounded-md border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-[1.5fr_1fr_1.5fr_auto_auto_auto] items-center gap-4 px-4 py-2 border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <div>Name</div>
          <div>Organization</div>
          <div>Scopes</div>
          <div>Created</div>
          <div>Last used</div>
          <div aria-hidden="true" className="w-20" />
        </div>
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : items.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            No tokens yet. Generate one from the Get started drawer.
          </div>
        ) : (
          items.map((item) => (
            <TokenRow
              key={item.id}
              token={item}
              onRevoked={() => setItems((prev) => prev.filter((t) => t.id !== item.id))}
              onRollback={() => void load()}
              onError={setActionError}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TokenRow({
  token,
  onRevoked,
  onRollback,
  onError,
}: {
  token: TokenItem;
  onRevoked: () => void;
  onRollback: () => void;
  onError: (msg: string) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const displayName = token.name ?? "Untitled token";
  const orgLabel = token.organizationName ?? (token.organizationId ? "—" : "All organizations");

  async function handleRevoke() {
    setBusy(true);
    // Optimistically drop the row — the modal closes immediately so the UI
    // feels snappy. On failure we re-fetch to restore the row and surface a
    // banner error.
    onRevoked();
    setConfirmOpen(false);
    try {
      await apiDelete(`/api/v1/me/tokens/${encodeURIComponent(token.id)}`);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to revoke token";
      onError(msg);
      onRollback();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-[1.5fr_1fr_1.5fr_auto_auto_auto] items-center gap-4 px-4 py-3 border-b border-border last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{displayName}</div>
        <div className="text-xs text-muted-foreground font-mono truncate">{token.id}</div>
      </div>
      <div className="min-w-0">
        <div className="text-sm text-foreground/80 truncate">{orgLabel}</div>
        {token.organizationSlug ? (
          <div className="text-xs text-muted-foreground font-mono truncate">
            {token.organizationSlug}
          </div>
        ) : null}
      </div>
      <div className="min-w-0">
        {token.scopes.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">full access</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {token.scopes.map((s) => (
              <span
                key={s}
                className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[11px] font-mono text-foreground/80"
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">
        {relativeTime(token.createdAt)}
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">
        {token.lastUsedAt ? relativeTime(token.lastUsedAt) : "never"}
      </div>
      <div className="w-20 text-right">
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={busy}
          aria-label={`Revoke ${displayName}`}
          className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
        >
          <Trash2 className="size-4" />
          Revoke
        </button>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Revoke ${displayName}?`}
        body={<p>This token will stop working immediately. This cannot be undone.</p>}
        confirmLabel="Revoke token"
        danger
        busy={busy}
        onConfirm={() => void handleRevoke()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
